import type { SVGProps } from 'react';
import { cn } from '@/components/ui';

/** Shared props: stroke/fill follow `currentColor` unless overridden for theme-aware icons. */
export type UiGlyphProps = SVGProps<SVGSVGElement>;

/** Chevron down (compact, 20×20 viewBox) — dropdowns, nav triggers. */
export function ChevronDownIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 1.6,
  width = 18,
  height = 18,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        d="M5 8.5L10 13.5L15 8.5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Check mark (20×20) — listbox selected row, menus. */
export function CheckIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 1.6,
  width = 16,
  height = 16,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        d="M6 10.5L8.5 13L14 7"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Chevron right (24×24 outline) — mobile accordions, carousels. */
export function ChevronRightIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 2,
  width = 24,
  height = 24,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth={strokeWidth}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

/** Chevron left (24×24 outline) — carousels. */
export function ChevronLeftIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 1.5,
  width = 24,
  height = 24,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth={strokeWidth}
        d="M15.75 19.5 8.25 12l7.5-7.5"
      />
    </svg>
  );
}

/** Chevron right wide (24×24, stroke 1.5) — matches carousel control art. */
export function ChevronRightWideIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 1.5,
  width = 24,
  height = 24,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth={strokeWidth}
        d="m8.25 4.5 7.5 7.5-7.5 7.5"
      />
    </svg>
  );
}

/** Arrow up (24×24) — scroll to top, “back to top”. */
export function ArrowUpIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 2.25,
  width = 24,
  height = 24,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth={strokeWidth}
        d="M4.5 15.75l7.5-7.5 7.5 7.5"
      />
    </svg>
  );
}

/** Hamburger menu (24×24). */
export function MenuHamburgerIcon({
  className,
  stroke = 'currentColor',
  strokeWidth = 2,
  width = 24,
  height = 24,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        stroke={stroke}
        strokeWidth={strokeWidth}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

/** Filled dismiss “X” (20×20) — announcement bars, compact close. */
export function DismissFilledIcon({
  className,
  fill = 'currentColor',
  width = 20,
  height = 20,
  ...rest
}: UiGlyphProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 20"
      aria-hidden
      className={cn(className)}
      {...rest}
    >
      <path
        fill={fill}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      />
    </svg>
  );
}
